import { auth } from '../firebase'; // Import Firebase auth instance
import { makeAuthenticatedRequest } from './api';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Special error thrown when attempting to add a franchisee whose name already exists.
 * Contains the existing franchisee's details so the caller can prompt the user.
 */
export class DuplicateFranchiseeNameError extends Error {
  constructor(message, existingFranchisee) {
    super(message);
    this.name = 'DuplicateFranchiseeNameError';
    this.existingFranchisee = existingFranchisee;
  }
}

/**
 * Fetches a list of franchisees from the backend.
 * @param {number} [pageSize=10] - The number of franchisees to fetch per page.
 * @param {string} [startAfterDocId] - The document ID to start fetching after (for pagination).
 * @returns {Promise<object>} - The response data containing franchisees and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listFranchisees = async (pageSize = 10, startAfterDocId = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/list`;
    const body = { pageSize };
    if (startAfterDocId) {
      body.startAfterDocId = startAfterDocId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch franchisees' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to list franchisees');
    }
    return data.data; // Return the nested data object { franchisees: [], nextPageDocId: '...' }

  } catch (error) {
    console.error("Error listing franchisees:", error);
    throw error; // Re-throw the error to be caught by the component
  }
};

/**
 * Adds a new franchisee via the backend API.
 * Requires fseCode, firstName, lastName, franchiseName, email, phoneNumber, abn, gstRegistered, bankAccountName, bankAccountNumber, country.
 * @param {object} franchiseeData - The data for the new franchisee (should contain fseCode, gstRegistered should be boolean).
 * @returns {Promise<object>} - The response data containing the newly added franchisee's ID.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addFranchisee = async (franchiseeData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/add`;

    // Reverted payload structure: fseCode, firstName, lastName are top-level
    const payload = {
        fseCode: franchiseeData.fseCode, // Was franchiseeData.franchiseCode before mapping in AddFranchiseePage
        firstName: franchiseeData.firstName,
        lastName: franchiseeData.lastName,
        franchiseName: franchiseeData.franchiseName,
        email: franchiseeData.email,
        phoneNumber: franchiseeData.phoneNumber,
        abn: franchiseeData.abn,
        gstRegistered: franchiseeData.gstRegistered, // Already boolean from AddFranchiseePage
        bankAccountName: franchiseeData.bankAccountName,
        bankAccountNumber: franchiseeData.bankAccountNumber,
        bsbNumber: franchiseeData.bsbNumber,
        country: franchiseeData.country,
        // Include optional fields if they exist in franchiseeData
        ...(franchiseeData.territory && { territory: franchiseeData.territory }),
        ...(franchiseeData.addressLine1 && { addressLine1: franchiseeData.addressLine1 }),
        ...(franchiseeData.addressLine2 && { addressLine2: franchiseeData.addressLine2 }),
        ...(franchiseeData.state && { state: franchiseeData.state }),
        ...(franchiseeData.postalCode && { postalCode: franchiseeData.postalCode }),
        ...(franchiseeData.xeroContactId && { xeroContactId: franchiseeData.xeroContactId }),
    };

    // Remove empty optional fields
    Object.keys(payload).forEach(key => {
        // Check for null or empty string, but allow gstRegistered: false
        if ((payload[key] === null || payload[key] === '') && !(key === 'gstRegistered' && payload[key] === false)) {
            delete payload[key];
        }
    });


    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload), // Send the reverted payload
    });

    const responseData = await response.json(); // Read response body once

    // Special case: duplicate franchise name – surface structured data to caller
    if (responseData.status === 'duplicate_name') {
      throw new DuplicateFranchiseeNameError(
        responseData.message || 'Franchise name already exists.',
        responseData.existingFranchisee
      );
    }

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to add franchisee');
    }

    // Assuming the API returns { status: 'success', data: { id: '...' } } or similar
    return responseData.data;

  } catch (error) {
    console.error("Error adding franchisee:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Fetches details for a specific franchisee.
 * @param {string} franchiseeId - The ID of the franchisee to fetch.
 * @returns {Promise<object>} - The franchisee data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getFranchiseeById = async (franchiseeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/get-by-id`; // Correct endpoint
    const payload = { franchiseeId }; // ID in the body

    const response = await fetch(url, {
      method: 'POST', // Correct method
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json', // Add Content-Type header
      },
      body: JSON.stringify(payload), // Send ID in the body
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to fetch franchisee details');
    }

    // Assuming the data is directly under responseData.data
    return responseData.data; // Return the franchisee data object

  } catch (error) {
    console.error(`Error fetching franchisee ${franchiseeId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing franchisee via the backend API.
 * @param {string} franchiseeId - The ID of the franchisee to update.
 * @param {object} updatedData - An object containing the fields to update.
 * @returns {Promise<object>} - The response data containing the updated franchisee's details.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const editFranchisee = async (franchiseeId, updatedData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required for editing.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/edit`;

    // Ensure gstRegistered is boolean if present
    const payload = {
      franchiseeId,
      ...updatedData,
      ...(updatedData.gstRegistered !== undefined && { gstRegistered: updatedData.gstRegistered === 'Yes' || updatedData.gstRegistered === true }),
      ...(updatedData.xeroContactId && { xeroContactId: updatedData.xeroContactId }),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to update franchisee');
    }

    return responseData.data; // Return the updated franchisee data

  } catch (error) {
    console.error(`Error updating franchisee ${franchiseeId}:`, error);
    throw error; // Re-throw the error
  }
};

/**
 * Deletes a franchisee via the backend API.
 * @param {string} franchiseeId - The ID of the franchisee to delete.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const deleteFranchisee = async (franchiseeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required for deletion.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/delete`;
    const payload = { franchiseeId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to delete franchisee');
    }

    // Return success message or data if needed
    return responseData; // e.g., { status: 'success', message: '...' }

  } catch (error) {
    console.error(`Error deleting franchisee ${franchiseeId}:`, error);
    throw error; // Re-throw the error
  }
};

/**
 * Searches for franchisees based on a search string.
 * @param {object} [options={}] - Optional parameters for search and pagination.
 * @param {string} [options.searchString=''] - The string to search for in franchisee names.
 * @param {number} [options.pageSize=5] - The number of franchisees to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @returns {Promise<object>} - The response data containing matching franchisees and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const searchFranchisees = async (options = {}) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated.");
    }
  
    const {
      searchString = '',
      pageSize = 5,
      startAfterDocId = null,
    } = options;
  
    if (!searchString || searchString.length < 3) {
        return { franchisees: [], nextPageDocId: null };
    }
  
    try {
      const idToken = await user.getIdToken();
      const url = `${BASE_URL}/fse/search`;
      const body = { searchString, pageSize };
      if (startAfterDocId) {
        body.startAfterDocId = startAfterDocId;
      }
  
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
  
      const responseData = await response.json();
  
      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }
  
      if (responseData.status !== 'success') {
          throw new Error(responseData.message || 'Failed to search franchisees');
      }
      // Ensure the response structure matches expectations
      return responseData.data || { franchisees: [], nextPageDocId: null };
  
    } catch (error) {
      console.error("Error searching franchisees:", error);
      return { franchisees: [], nextPageDocId: null, error: error.message };
    }
  };

/**
 * Fetches the list of allowed franchisee emails from the backend with optional search and pagination.
 * @param {object} [options={}] - Optional parameters.
 * @param {number} [options.pageSize=20] - Number of results per page.
 * @param {string|null} [options.startAfterDocId=null] - Pagination cursor (doc ID).
 * @param {string} [options.search=''] - Search term (email prefix, min 2 chars to activate).
 * @returns {Promise<{emails: Array, nextPageDocId: string|null}>}
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listAllowedEmails = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const { pageSize = 20, startAfterDocId = null, search = '' } = options;

  try {
    const idToken = await user.getIdToken();
    const params = new URLSearchParams({ pageSize, search });
    if (startAfterDocId) params.set('startAfterDocId', startAfterDocId);
    const url = `${BASE_URL}/allowed-emails/list?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list allowed emails');
    }

    // Return the full data object so callers can access nextPageDocId
    return responseData.data || { emails: [], nextPageDocId: null };

  } catch (error) {
    console.error("Error listing allowed emails:", error);
    throw error;
  }
};

/**
 * Adds an email to the allowed list via the backend API.
 * @param {string} email - The email address to add.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addAllowedEmail = async (email, firstName, lastName) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!email) {
    throw new Error("Email is required.");
  }
  // The welcome email greets by first name, so neither may be blank
  if (!firstName) {
    throw new Error("First name is required.");
  }
  if (!lastName) {
    throw new Error("Last name is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-emails/add`;
    const payload = { email, firstName, lastName };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData; // Return the full response { status, message, data }

  } catch (error) {
    console.error("Error adding allowed email:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Removes an email from the allowed list via the backend API.
 * @param {string} email - The email address to remove.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const removeAllowedEmail = async (email) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
   if (!email) {
    throw new Error("Email is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/allowed-emails/remove`;
    const payload = { email };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData; // Return the full response { status, message }

  } catch (error) {
    console.error("Error removing allowed email:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Reset password for a franchisee via the backend API.
 * @param {string} userEmail - The email address of the franchisee to reset password for.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const resetFranchiseePassword = async (userEmail) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!userEmail) {
    throw new Error("User email is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/auth/reset-password`;
    const payload = { userEmail };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to reset password');
    }

    return responseData; // Return the full response { status, message, data }

  } catch (error) {
    console.error("Error resetting franchisee password:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Updates a franchisee's status (active/suspended).
 * @param {string} franchiseeId - The ID of the franchisee to update.
 * @param {boolean} isActive - The new active status.
 * @returns {Promise<object>} - The response data from the backend.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const updateFranchiseeStatus = async (franchiseeId, isActive) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/update-status`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        franchiseeId,
        isActive
      }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to update franchisee status');
    }

    console.log(`[INFO] Franchisee status updated successfully - ID: ${franchiseeId}, Status: ${isActive ? 'Active' : 'Suspended'}`);
    return responseData;

  } catch (error) {
    console.error('Error updating franchisee status:', error);
    throw error;
  }
};

/**
 * Creates a Xero contact from franchisee data via the backend API.
 * @param {string} franchiseeId - The franchisee document ID
 * @returns {Promise<{franchiseeId, xeroContactId, contactName, createdAt}>}
 * @throws {Error} if the request fails
 */
export const createXeroContactFromFranchisee = async (franchiseeId) => {
  const user = auth.currentUser;
  console.log('user', user)
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/create-xero-contact`;
    const payload = { franchiseeId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to create Xero contact');
    }

    return responseData.data; // Returns { franchiseeId, xeroContactId, contactName, createdAt }

  } catch (error) {
    console.error(`Error creating Xero contact for franchisee ${franchiseeId}:`, error);
    throw error;
  }
};

export const previewXeroContactFromFranchisee = async (franchiseeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/preview-xero-contact`;
    const payload = { franchiseeId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to preview Xero contact');
    }

    return responseData.data;
  } catch (error) {
    console.error(`Error previewing Xero contact for franchisee ${franchiseeId}:`, error);
    throw error;
  }
};

/**
 * Lists active franchisees that have no linked Xero contact. Read-only.
 * @returns {Promise<{count: number, franchisees: Array}>}
 */
export const listFranchiseesMissingXeroContact = async () =>
  makeAuthenticatedRequest('/fse/missing-xero-contacts', { method: 'POST', body: {} });

/**
 * Creates (or links) Xero contacts for the given franchisees.
 * @param {string[]} franchiseeIds - The franchisee document IDs to process.
 * @returns {Promise<{totalProcessed, totalSuccess, totalFailed, results}>}
 */
export const createXeroContactsForFranchisees = async (franchiseeIds) => {
  if (!Array.isArray(franchiseeIds) || franchiseeIds.length === 0) {
    throw new Error("At least one franchisee is required.");
  }

  return makeAuthenticatedRequest('/fse/create-missing-xero-contacts', {
    method: 'POST',
    body: { franchiseeIds },
  });
};

/**
 * Reads what Loope holds, what Xero holds, and whether they agree (ticket 100). Read-only.
 * @param {string} franchiseeId - The ID of the franchisee.
 * @returns {Promise<object>} - { bsbNumber, bankAccountNumber, wouldSend, reason, sendable, xeroContactId, xeroHas, verdict }
 */
export const getXeroBankDetailsStatus = async (franchiseeId) => {
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  return makeAuthenticatedRequest('/fse/xero-bank-details-status', {
    method: 'POST',
    body: { franchiseeId },
  });
};

/**
 * Resends Loope's bank details to the franchisee's existing Xero contact (ticket 100).
 * @param {string} franchiseeId - The ID of the franchisee.
 * @returns {Promise<object>} - The API response, carrying a message on failure.
 */
export const syncXeroBankDetails = async (franchiseeId) => {
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  return makeAuthenticatedRequest('/fse/sync-xero-bank-details', {
    method: 'POST',
    body: { franchiseeId },
  });
};

/**
 * Updates compliance data for a franchisee (e.g. police certificate expiry date).
 * @param {string} franchiseeId - The ID of the franchisee.
 * @param {object} complianceData - Partial compliance data to update (e.g. { policeCertificateExpiryDate }).
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const updateFranchiseeCompliance = async (franchiseeId, complianceData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!franchiseeId) {
    throw new Error("Franchisee ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/fse/compliance/update`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ franchiseeId, complianceData }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to update compliance data');
    }

    return responseData.data;
  } catch (error) {
    console.error(`Error updating compliance for franchisee ${franchiseeId}:`, error);
    throw error;
  }
};
